import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FolderPlus, AlertCircle, Lock, Globe, Loader2 } from "lucide-react";

interface CreateProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    name: string;
    description: string;
    isPrivate: boolean;
  }) => Promise<void>;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Auto-reset form when modal closes
  useEffect(() => {
    if (!open) {
      setName("");
      setDescription("");
      setIsPrivate(false);
      setIsSubmitting(false);
      setNameError(null);
      setSubmitError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    // Validate
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Project name is required");
      return;
    }
    if (trimmed.length > 100) {
      setNameError("Project name must be 100 characters or fewer");
      return;
    }

    setNameError(null);
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({ name: trimmed, description, isPrivate });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to create project"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSubmitting) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const nameLength = name.length;
  const charCountColor =
    nameLength > 100
      ? "text-destructive"
      : nameLength >= 80
        ? "text-amber-500"
        : "text-muted-foreground";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="sm:max-w-md p-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <FolderPlus className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-card-foreground">
                Create new project
              </h3>
              <p className="text-sm text-muted-foreground">
                Start building your next application with TerraMatrix's visual
                editor.
              </p>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-border" />

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Project Name */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="create-project-name"
                className="text-sm font-medium text-card-foreground"
              >
                Project name <span className="text-destructive">*</span>
              </Label>
              <span className={`text-xs tabular-nums ${charCountColor}`}>
                {nameLength}/100
              </span>
            </div>
            <Input
              id="create-project-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              onKeyDown={handleNameKeyDown}
              placeholder="e.g. Customer Dashboard"
              className={`h-10 bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary ${
                nameError
                  ? "border-destructive focus:border-destructive focus:ring-destructive"
                  : ""
              }`}
              autoFocus
              disabled={isSubmitting}
            />
            {nameError && (
              <div className="flex items-center gap-1.5 text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <p className="text-xs">{nameError}</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label
              htmlFor="create-project-description"
              className="text-sm font-medium text-card-foreground"
            >
              Description{" "}
              <span className="text-muted-foreground font-normal">(Optional)</span>
            </Label>
            <Textarea
              id="create-project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What will you build with this project?"
              rows={3}
              className="resize-none bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary"
              disabled={isSubmitting}
            />
          </div>

          {/* Privacy Setting Card */}
          <div className="rounded-lg border border-border bg-muted p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isPrivate ? (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Globe className="h-4 w-4 text-muted-foreground" />
                )}
                <div>
                  <p className="text-sm font-medium text-card-foreground">
                    {isPrivate ? "Private project" : "Public project"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isPrivate
                      ? "Only you and invited collaborators can access this project."
                      : "Anyone in your organization can view this project."}
                  </p>
                </div>
              </div>
              <Switch
                checked={isPrivate}
                onCheckedChange={setIsPrivate}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Submit Error Banner */}
          {submitError && (
            <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">{submitError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 px-6 py-4 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="sm:w-auto w-full border-border text-card-foreground hover:bg-muted"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !name.trim()}
            className="sm:w-auto w-full min-w-[120px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              "Create Project"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
